import React from 'react';
import { ArrowRightIcon, CheckIcon } from 'lucide-react';
import { inspirationIdeas, organizedNeeds, painQuestions, patternEssentials, productName, systemFlow } from '../data/content';
import { Mark, SectionTitle } from './ui/Section';
export function ProblemSolution() {
  return <section className="bg-white px-4 py-12 md:py-16" data-id="element-179" data-mp="3hrq71tb1efhy">
      <div className="mx-auto max-w-5xl" data-id="element-180" data-mp="3ht9q1ro2v4nf">
        <SectionTitle data-id="element-181" data-mp="icl4k963n4ds">
          What if your next crochet bag was <Mark data-id="element-182" data-mp="ie5zw108mrcp">already planned out for you?</Mark>
        </SectionTitle>
        <div className="mx-auto mt-5 max-w-2xl space-y-3 text-center text-sm leading-relaxed text-muted md:text-base" data-id="element-183" data-mp="ebrlpu7ikfrr">
          <p className="font-bold text-plum-950" data-id="element-184" data-mp="9hwzaj0ixmoz">You already know how to crochet.</p>
          <p data-id="element-185" data-mp="4k5a3wr9keoh">
            Then you spot a gorgeous crochet handbag online. You love the shape, the texture, the colors — you can
            already picture it finished.
          </p>
          <p data-id="element-186" data-mp="x8kbvhldx0hv">But when you go looking for the instructions, the details you need just aren't there.</p>
        </div>

        <ul className="mx-auto mt-6 max-w-xl space-y-2" data-id="element-187" data-mp="6t2d58wqf0xt">
          {painQuestions.map(question => <li key={question} className="border-l-4 border-berry-500 bg-blush-50 px-4 py-2.5 text-left text-sm font-bold text-plum-950 md:text-base" data-id="element-188" data-mp="bqu3vzln4h0f">
              {question}
            </li>)}
        </ul>

        <div className="mx-auto mt-6 max-w-2xl space-y-3 text-center text-sm leading-relaxed text-muted md:text-base" data-id="element-189" data-mp="3k75p7zgp9f10">
          <p data-id="element-190" data-mp="3p7b8osctfv80">
            Often all you find is a pretty photo or a short video that skips the yarn quantities, stitch counts,
            measurements, and assembly steps.
          </p>
          <p data-id="element-191" data-mp="3f9dnyeu60gk5">
            That's exactly why we created the <strong className="text-plum-950" data-id="element-192" data-mp="3fa5j5ojuoc9w">{productName}</strong>: 105 crochet
            projects with complete construction guidance, so you can focus on what you enjoy most — making beautiful
            things.
          </p>
        </div>
        <p className="mt-5 text-center font-display text-sm font-black uppercase tracking-[.2em] text-berry-700" data-id="element-193" data-mp="2me61gro31n5n">
          Pick. Crochet. Assemble. Enjoy.
        </p>

        <div className="mt-12 grid gap-5 md:grid-cols-2" data-id="element-194" data-mp="2dd7z0dtalnp6">
          <div className="flex flex-col rounded-2xl border border-berry-600/15 bg-blush-50 p-6" data-id="element-195" data-mp="2ddrdfkjqhtyg">
            <h3 className="text-base font-black uppercase leading-tight text-plum-950 md:text-lg" data-id="element-196" data-mp="28fpm6cc5mobw">
              Beautiful crochet ideas deserve complete instructions.
            </h3>
            <div className="mt-3 space-y-2 text-sm leading-relaxed text-muted" data-id="element-197" data-mp="3143lcvezbqp1">
              <p data-id="element-198" data-mp="2wbaea0w1g69s">Your saved crochet inspiration folder is probably full of ideas:</p>
              <p className="italic text-plum-900" data-id="element-199" data-mp="2rdip3ems2y9a">{inspirationIdeas.join(' ')}</p>
              <p data-id="element-200" data-mp="3osee1baiuk28">
                But a picture alone won't tell you how much yarn to buy, how many stitches to start with, or how the
                pieces fit together.
              </p>
              <p className="font-bold text-plum-950" data-id="element-201" data-mp="3jztmkgf38j1v">You need a pattern you can actually follow.</p>
              <p className="font-semibold text-plum-950" data-id="element-202" data-mp="3rf5owmeajgne">
                Every design in the {productName} is planned around the essentials:
              </p>
            </div>
            <ul className="mt-2 flex-1 space-y-1.5" data-id="element-203" data-mp="54b52m6erz9s">
              {patternEssentials.map(item => <li key={item} className="flex items-start gap-2 text-sm text-plum-950" data-id="element-204" data-mp="98iuymolrl9l">
                  <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-berry-600" aria-hidden="true" data-id="element-205" data-mp="e60wvt47d083" />
                  {item}
                </li>)}
            </ul>
            <p className="mt-5 text-xs font-black uppercase tracking-wider text-berry-700" data-id="element-206" data-mp="nx5ez1p6d175">
              More Beautiful Designs. More Complete Patterns. More Projects to Make.
            </p>
          </div>

          <div className="flex flex-col rounded-2xl bg-plum-900 p-6" data-id="element-207" data-mp="wy3kjof9hgru">
            <h3 className="text-base font-black uppercase leading-tight text-white md:text-lg" data-id="element-208" data-mp="11v3ontsjw9gb">
              One bundle. <span className="text-blush-300" data-id="element-209" data-mp="o838ef4eeclc">Endless creative possibilities.</span>
            </h3>
            <ul className="mt-4 flex-1 space-y-2.5" data-id="element-210" data-mp="k6i1rbigiujl">
              {organizedNeeds.map(item => <li key={item.question} className="flex items-start gap-2 text-sm text-white/85" data-id="element-211" data-mp="xxwkricd6vqn">
                  <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-blush-300" aria-hidden="true" data-id="element-212" data-mp="t0web6blrqz0" />
                  <span data-id="element-213" data-mp="5bbwiqr6qzrb">
                    <strong className="font-bold text-white" data-id="element-214" data-mp="5ak7xzw0a2aj">{item.question}</strong> {item.answer}
                  </span>
                </li>)}
            </ul>
            <p className="mt-5 text-sm font-semibold text-white" data-id="element-215" data-mp="f99gv35xccen">
              Everything is organized into a complete 10-book digital collection.
            </p>
            <ol className="mt-4 flex flex-wrap items-center gap-1.5" data-id="element-216" data-mp="1qnd9o60rzui4">
              {systemFlow.map((step, index) => <li key={step} className="flex items-center gap-1.5" data-id="element-217" data-mp="1lpbtbsuqi9ac" data-mpi={index}>
                  {index > 0 && <ArrowRightIcon className="h-3.5 w-3.5 text-blush-300" aria-hidden="true" data-id="element-218" data-mp="33lnilxrtbpi3" />}
                  <span className="whitespace-nowrap rounded-md bg-blush-200 px-2 py-1 text-[10px] font-black uppercase tracking-wide text-plum-950" data-id="element-219" data-mp="38lrpcw2x55v4">
                    {step}
                  </span>
                </li>)}
            </ol>
          </div>
        </div>
      </div>
    </section>;
}